import { MEAL_TYPES } from '@/types/constants'

export type RestaurantMealType =
  | typeof MEAL_TYPES.BREAKFAST
  | typeof MEAL_TYPES.LUNCH
  | typeof MEAL_TYPES.EVENING
  | typeof MEAL_TYPES.DINNER

type MealWindowEntry = {
  open?: string
  close?: string
  isOpen?: boolean
}

type MealWindowMap = Record<string, MealWindowEntry[]>

const mealTypeConfig: Array<{
  mealType: RestaurantMealType
  apiKey: string
}> = [
  { mealType: MEAL_TYPES.BREAKFAST, apiKey: 'Breakfast' },
  { mealType: MEAL_TYPES.LUNCH, apiKey: 'Lunch' },
  { mealType: MEAL_TYPES.EVENING, apiKey: 'Evening' },
  { mealType: MEAL_TYPES.DINNER, apiKey: 'Dinner' },
]

function parseTimeToMinutes(raw: string | undefined): number | null {
  if (!raw) return null

  const trimmed = raw.trim()
  if (!trimmed) return null

  const digitsOnly = trimmed.replace(/[^\d]/g, '')

  if (digitsOnly.length === 4) {
    const hours = Number(digitsOnly.slice(0, 2))
    const minutes = Number(digitsOnly.slice(2, 4))
    if (Number.isNaN(hours) || Number.isNaN(minutes) || hours > 23 || minutes > 59) return null
    return hours * 60 + minutes
  }

  if (trimmed.includes(':')) {
    const [hourPart, minutePart] = trimmed.split(':')
    const hours = Number(hourPart)
    const minutes = Number(minutePart)
    if (Number.isNaN(hours) || Number.isNaN(minutes) || hours > 23 || minutes > 59) return null
    return hours * 60 + minutes
  }

  return null
}

export function parseRestaurantMealTime(rawMealTime?: string | null): MealWindowMap | null {
  if (!rawMealTime) return null

  try {
    const parsed = JSON.parse(rawMealTime) as unknown
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null
    }

    return parsed as MealWindowMap
  } catch {
    return null
  }
}

export function resolveCurrentRestaurantMealType(
  rawMealTime?: string | null,
  now: Date = new Date()
): RestaurantMealType | null {
  const parsedMealTime = parseRestaurantMealTime(rawMealTime)
  if (!parsedMealTime) return null

  const currentMinutes = now.getHours() * 60 + now.getMinutes()

  for (const config of mealTypeConfig) {
    const windows = parsedMealTime[config.apiKey]
    if (!Array.isArray(windows) || windows.length === 0) continue

    const hasMatch = windows.some(window => {
      if (!window || typeof window !== 'object' || window.isOpen === false) {
        return false
      }

      const openMinutes = parseTimeToMinutes(window.open)
      const closeMinutes = parseTimeToMinutes(window.close)

      if (openMinutes == null || closeMinutes == null) {
        return false
      }

      if (closeMinutes >= openMinutes) {
        return currentMinutes >= openMinutes && currentMinutes < closeMinutes
      }

      return currentMinutes >= openMinutes || currentMinutes < closeMinutes
    })

    if (hasMatch) {
      return config.mealType
    }
  }

  return null
}
